/* eslint-disable no-console */
import NotificationSubscriptions from '../api/notificationSubscription';
import auth from '../api/auth';
import { useAlert } from 'dashboard/composables';
import { translate } from './bonbozTranslate';
import { detectOS, OS } from 'shared/helpers/platform';
import { getDeviceId, getDeviceInfo } from './deviceIdentity';

export const verifyServiceWorkerExistence = (callback = () => {}) => {
  if (!('serviceWorker' in navigator)) {
    // Service Worker isn't supported on this browser, disable or hide UI.
    return;
  }

  if (!('PushManager' in window)) {
    // Push isn't supported on this browser, disable or hide UI.
    return;
  }

  navigator.serviceWorker
    .register('/sw.js')
    .then(registration => callback(registration))
    .catch(registrationError => {
      // eslint-disable-next-line
      console.log('SW registration failed: ', registrationError);
    });
};

// BonBoZ: bản promise của `verifyServiceWorkerExistence`, trả null khi máy
// không hỗ trợ push hoặc đăng ký service worker lỗi.
export const getPushRegistration = () => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return Promise.resolve(null);
  }
  return navigator.serviceWorker.register('/sw.js').catch(error => {
    console.warn('SW registration failed:', error);
    return null;
  });
};

export const getCurrentPushSubscription = () =>
  getPushRegistration().then(
    registration => registration?.pushManager.getSubscription() || null
  );

export const hasPushPermissions = () => {
  if ('Notification' in window) {
    return Notification.permission === 'granted';
  }
  return false;
};

// 'default' | 'granted' | 'denied', hoặc 'unsupported' khi không có
// `Notification` (vd iPhone mở trong tab Safari).
export const getNotificationPermission = () =>
  'Notification' in window ? Notification.permission : 'unsupported';

// Trình duyệt có đủ những gì Web Push cần.
export const isPushSupported = () =>
  'Notification' in window &&
  'serviceWorker' in navigator &&
  'PushManager' in window;

// Trạng thái thật của máy này: quyền + đăng ký push đang có (hoặc null).
export const readPushState = async () => ({
  permission: getNotificationPermission(),
  subscription: await getCurrentPushSubscription(),
});

// BonBoZ: iPhone/iPad đang mở dashboard trong TAB trình duyệt, chưa cài ra màn
// hình chính.
//
// Trên iOS, Web Push chỉ có trong app đã "Thêm vào MH chính" (iOS 16.4+); trong
// tab Safari thì `window.Notification` không tồn tại. Không tách riêng trường
// hợp này thì nhân viên đọc câu "trình duyệt không hỗ trợ" và kết luận iPhone
// không dùng được, trong khi chỉ thiếu một bước cài. `matchMedia` có thể vắng
// (môi trường test, trình duyệt cũ) nên gọi qua `?.`.
export const isIOSBrowserTab = () => {
  if (detectOS() !== OS.IOS) return false;
  const standalone =
    window.navigator.standalone === true ||
    !!window.matchMedia?.('(display-mode: standalone)')?.matches;
  return !standalone;
};

// BonBoZ: cờ "người dùng tự gạt tắt thông báo đẩy trên máy này".
//
// Lúc mở app, máy đã cấp quyền mà chưa có đăng ký thì được đăng ký lại âm thầm
// (xem `restorePushSubscription`). Không có cờ này thì người vừa chủ động tắt
// sẽ bị bật lại ở lần mở app kế tiếp.
const PUSH_OPT_OUT_KEY = 'bonboz_tu_tat_thong_bao_day';

export const hasPushOptOut = () => {
  try {
    return localStorage.getItem(PUSH_OPT_OUT_KEY) === '1';
  } catch (error) {
    // Không đọc được lưu trữ (cửa sổ riêng tư) — coi như chưa tự tắt.
    return false;
  }
};

export const setPushOptOut = optedOut => {
  try {
    if (optedOut) {
      localStorage.setItem(PUSH_OPT_OUT_KEY, '1');
    } else {
      localStorage.removeItem(PUSH_OPT_OUT_KEY);
    }
  } catch (error) {
    // Không ghi được thì lần mở app sau có thể tự đăng ký lại, chấp nhận được.
  }
};

const generateKeys = str =>
  btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

export const getPushSubscriptionPayload = subscription => ({
  subscription_type: 'browser_push',
  subscription_attributes: {
    endpoint: subscription.endpoint,
    p256dh: generateKeys(subscription.getKey('p256dh')),
    auth: generateKeys(subscription.getKey('auth')),
  },
});

export const sendRegistrationToServer = async subscription => {
  if (!auth.hasAuthCookie()) return null;
  return NotificationSubscriptions.create({
    notification_subscription: {
      ...getPushSubscriptionPayload(subscription),
      ...(await getDeviceInfo()),
    },
  });
};

// BonBoZ: lỗi tạm thời của dịch vụ đăng ký push của Google (Chrome Android).
// Vài phút sau thử lại thường được, nên đáng tự thử lại.
export const isPushServiceError = error =>
  error?.name === 'AbortError' && /push service error/i.test(error?.message);

// Thử lại đúng một lần khi người dùng bấm bật mà gặp `isPushServiceError`.
export const MANUAL_RETRY_DELAY_MS = 3 * 1000;

const SUBSCRIBE_ERROR_MAX_LENGTH = 120;

// Lỗi đăng ký gần nhất trên máy này, gửi kèm `device_status` để report chỉ ra
// máy nào đang kẹt. Đăng ký thành công thì xoá.
let lastSubscribeError = null;

export const formatSubscribeError = error =>
  `${error?.name || 'Error'}: ${error?.message || error}`.slice(
    0,
    SUBSCRIBE_ERROR_MAX_LENGTH
  );

export const getLastSubscribeError = () => lastSubscribeError;

const recordSubscribeResult = error => {
  lastSubscribeError = error ? formatSubscribeError(error) : null;
};

const wait = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

// Một lượt đăng ký tại một thời điểm: bấm tay trùng lúc app tự thử lại thì
// dùng chung lượt đang chạy.
let pendingSubscribe = null;

const subscribeAndSend = () => {
  pendingSubscribe ||= navigator.serviceWorker.ready
    .then(serviceWorkerRegistration =>
      serviceWorkerRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: window.chatwootConfig.vapidPublicKey,
      })
    )
    .then(sendRegistrationToServer)
    .finally(() => {
      pendingSubscribe = null;
    });
  return pendingSubscribe;
};

const subscribeWithManualRetry = async () => {
  try {
    return await subscribeAndSend();
  } catch (error) {
    if (!isPushServiceError(error)) throw error;
    await wait(MANUAL_RETRY_DELAY_MS);
    return subscribeAndSend();
  }
};

const subscribeErrorMessage = error =>
  isPushServiceError(error)
    ? translate('BONBOZ.QUYEN_THONG_BAO.PUSH_SERVICE_BUSY')
    : `${translate('BONBOZ.QUYEN_THONG_BAO.DANG_KY_LOI')} ${error.name || 'Error'}: ${error.message || error}`;

// `silent`: chỉ ghi console, không hiện thông báo và không tự thử lại — dùng
// khi app tự đăng ký, việc thử lại do `pushRecovery.js` lo.
// Trả về lỗi gặp phải, hoặc `null` khi thành công.
export const registerSubscription = async (
  onSuccess = () => {},
  onError = () => {},
  { silent = false } = {}
) => {
  if (!window.chatwootConfig.vapidPublicKey) {
    // BonBoZ: NOI ra thay vi im lang.
    //
    // Ban goc `return` khong ke gi: nguoi dung gat cong tac, khong co gi xay ra,
    // va lan sau vao lai thay cong tac da tu tat. Khong mot manh moi nao de lan.
    // Den day nghia la may chu khong dua ra khoa VAPID dung chuan — xem
    // `lib/vapid_service.rb` va SETUP.md 2.28.
    const error = Object.assign(new Error('missing VAPID public key'), {
      name: 'VapidKeyMissing',
    });
    recordSubscribeResult(error);
    if (silent) {
      console.warn('Push subscription skipped: missing VAPID public key');
    } else {
      useAlert(translate('BONBOZ.QUYEN_THONG_BAO.MAY_CHU_THIEU_KHOA'));
    }
    onError(error);
    return error;
  }
  try {
    await (silent ? subscribeAndSend() : subscribeWithManualRetry());
    recordSubscribeResult(null);
    onSuccess();
    return null;
  } catch (error) {
    console.error('Push subscription registration failed:', error);
    recordSubscribeResult(error);
    // BonBoZ: hiện nguyên nhân thật thay vì câu "trình duyệt không hỗ trợ".
    //
    // Cái `catch` này bắt CẢ BA bước — chờ service worker, đăng ký với dịch vụ
    // đẩy, gửi đăng ký lên server — nhưng upstream thay mọi lỗi bằng đúng một
    // câu, mà câu đó lại đổ tại trình duyệt. Đã mất một buổi (09/09/2026) để
    // lần ra rằng Chrome hỗ trợ đầy đủ, lỗi thật nằm chỗ khác: khoá VAPID rỗng
    // trên server thì `subscribe()` ném `InvalidAccessError`, máy đăng ký sẵn
    // bằng khoá khác thì ném `InvalidStateError`, máy thiếu Play Services thì
    // `AbortError`. Tên lỗi là thứ duy nhất phân biệt được chúng nên phải hiện
    // ra. Riêng lỗi bận tạm thời của Google thì báo câu dễ hiểu.
    if (!silent) useAlert(subscribeErrorMessage(error));
    onError(error);
    return error;
  }
};

// Xin quyền VÀ đăng ký nhận push trong cùng một cú bấm. Cho phép thông báo
// mới là một nửa: phải có đăng ký với dịch vụ đẩy thì server mới biết gửi về
// máy nào lúc app đóng. Mọi nút "Bật thông báo" đều phải đi qua hàm này.
//
// `onError` được gọi ở MỌI nhánh không thành, để chỗ gọi trả công tắc về tắt
// thay vì để nó sáng mà không có gì phía sau. Trả về promise để chỗ gọi đọc
// lại `Notification.permission` sau khi xong.
export const requestPushPermissions = ({
  onSuccess = () => {},
  onError = () => {},
} = {}) => {
  const succeed = () => {
    // Người dùng chủ động bật lại thì bỏ cờ "tự tắt".
    setPushOptOut(false);
    onSuccess();
  };
  // Google bận: người dùng vẫn muốn bật, bỏ cờ tự tắt để app tự thử lại ở nền.
  const fail = error => {
    if (isPushServiceError(error)) setPushOptOut(false);
    onError(error);
  };

  if (!('Notification' in window)) {
    // eslint-disable-next-line no-console
    console.warn('Notification is not supported');
    // BonBoZ: iPhone mở trong tab thì không phải thiếu hỗ trợ mà là chưa cài
    // app ra màn hình chính — chỉ cách cài. Còn lại thì câu "không hỗ trợ" là
    // đúng: không có `Notification` là trình duyệt thật sự thiếu.
    useAlert(
      translate(
        isIOSBrowserTab()
          ? 'BONBOZ.QUYEN_THONG_BAO.CAN_CAI_APP_IOS'
          : 'BONBOZ.QUYEN_THONG_BAO.KHONG_HO_TRO'
      )
    );
    onError();
    return Promise.resolve();
  }

  if (Notification.permission === 'granted') {
    return registerSubscription(succeed, fail);
  }

  if (Notification.permission === 'denied') {
    // Trình duyệt không hỏi lại lần hai; im lặng ở đây là người dùng không
    // biết phải tự mở khoá trong cài đặt.
    useAlert(translate('BONBOZ.QUYEN_THONG_BAO.BI_CHAN'));
    onError();
    return Promise.resolve();
  }

  return Notification.requestPermission().then(permission => {
    if (permission === 'granted') {
      return registerSubscription(succeed, fail);
    }
    if (permission === 'denied') {
      useAlert(translate('BONBOZ.QUYEN_THONG_BAO.BI_CHAN'));
    }
    // Còn 'default' là người dùng đóng hộp thoại mà không chọn — không báo gì.
    onError();
    return undefined;
  });
};

// BonBoZ: đồng bộ đăng ký push lúc mở app và mỗi lần quay lại app. Không có
// cú bấm nên KHÔNG BAO GIỜ hiện lỗi, chỉ ghi console.
//
// Đã có đăng ký thì chỉ gửi lại lên server (khi `resendExisting`), không gọi
// `subscribe()`: lúc quyền bị chặn, iOS đòi cú bấm cho `subscribe()`. Chưa có
// mà quyền đã cấp thì đăng ký âm thầm (Chrome Android bỏ đăng ký khi mở app lúc
// đang chặn). Máy đang chặn hoặc đã tự gạt tắt (`hasPushOptOut`) thì bỏ qua.
//
// Trả `{ attempted, error }`: `attempted` khi đã gọi `subscribe()`, `error` là
// lỗi của lần đó (hoặc null) để `pushRecovery.js` quyết định thử lại.
export const restorePushSubscription = async ({
  resendExisting = true,
} = {}) => {
  const skipped = { attempted: false, error: null };
  try {
    const registration = await getPushRegistration();
    if (!registration || hasPushOptOut() || !hasPushPermissions()) {
      return skipped;
    }

    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      recordSubscribeResult(null);
      if (resendExisting) await sendRegistrationToServer(subscription);
      return skipped;
    }
    const error = await registerSubscription(
      () => {},
      () => {},
      { silent: true }
    );
    return { attempted: true, error };
  } catch (error) {
    console.warn('Push subscription restore failed:', error);
    return skipped;
  }
};

// BonBoZ: người dùng tự tắt thông báo trên máy này.
//
// Báo server trước (`disabled_in_app`), thành công mới huỷ đăng ký trên máy và
// đặt cờ tự tắt. Huỷ trước mà server không biết thì server vẫn gửi push vào
// đăng ký đã chết. Trả `false` khi không báo được server (mất mạng): chỗ gọi
// giữ công tắc bật.
export const disablePushNotifications = async () => {
  const subscription = await getCurrentPushSubscription().catch(() => null);
  if (subscription) {
    try {
      await NotificationSubscriptions.disable({
        endpoint: subscription.endpoint,
        device_id: getDeviceId(),
      });
    } catch (error) {
      // 404: server không biết đăng ký này, không có gì để chặn, tắt tiếp.
      if (error?.response?.status !== 404) {
        console.warn('Push disable request failed:', error);
        return false;
      }
    }
    try {
      await subscription.unsubscribe();
    } catch (error) {
      // Server đã ngừng gửi; cờ tự tắt chặn việc gửi lại đăng ký này.
      console.warn('Push unsubscribe failed:', error);
    }
  }
  setPushOptOut(true);
  return true;
};
